import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useRegion } from '../hooks/useRegion';
import { Card, CardContent, CardHeader } from './ui/card';
import { Loader2 } from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

export function ROIMiniCard({ disableLink = false }) {
  const navigate = useNavigate();
  const { roiProjection, roiProjectionLoading, fetchROIProjection } = useAppStore();
  const { formatCurrency } = useRegion();
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(800);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-parchment text-foreground rounded-cards px-3 py-2 shadow-modal">
          <p className="text-xs text-muted-foreground">{data.period}</p>
          <p className="text-sm">
            {formatCurrency(data.income)}
          </p>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    fetchROIProjection();
  }, [fetchROIProjection]);

  // Observe container width for responsive grouping
  useEffect(() => {
    if (!chartContainerRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setChartWidth(entries[0].contentRect.width);
      }
    });
    
    observer.observe(chartContainerRef.current);
    return () => observer.disconnect();
  }, []);

  // Build mini chart data with period-based income bars and cumulative line
  const chartData = useMemo(() => {
    if (!roiProjection?.has_data) return [];
    
    const { portfolio, historical_data } = roiProjection;
    const target = portfolio.total_value;
    
    if (!historical_data || historical_data.length === 0) return [];
    
    // Determine grouping based on width and data span
    const dates = historical_data.map(d => new Date(d.date));
    const minYear = Math.min(...dates.map(d => d.getFullYear()));
    const maxYear = Math.max(...dates.map(d => d.getFullYear()));
    const yearSpan = maxYear - minYear;
    
    let grouping = 'month'; // default
    if (yearSpan > 3) {
      grouping = 'year';
    } else if (chartWidth < 600) {
      grouping = 'quarter';
    }
    
    // Helper functions for period keys and labels
    const getPeriodKey = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      
      if (grouping === 'year') {
        return `${year}`;
      } else if (grouping === 'quarter') {
        const quarter = Math.floor(month / 3) + 1;
        return `${year}-Q${quarter}`;
      } else {
        return `${year}-${String(month + 1).padStart(2, '0')}`;
      }
    };
    
    const getPeriodLabel = (periodKey) => {
      if (grouping === 'year') {
        return periodKey;
      } else if (grouping === 'quarter') {
        const [year, q] = periodKey.split('-');
        return `${q} '${year.slice(2)}`;
      } else {
        const [year, month] = periodKey.split('-');
        const date = new Date(year, parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }
    };
    
    // Group by period, keeping the highest cumulative value for each period
    const periodData = {};
    
    historical_data.forEach(d => {
      const date = new Date(d.date);
      const periodKey = getPeriodKey(date);
      
      if (!periodData[periodKey] || d.income > periodData[periodKey].cumulative) {
        periodData[periodKey] = {
          cumulative: d.income,
          date: date
        };
      }
    });
    
    // Sort periods and calculate period income
    const sortedPeriods = Object.keys(periodData).sort((a, b) => {
      return periodData[a].date - periodData[b].date;
    });
    
    const data = [];
    let previousCumulative = 0;
    
    sortedPeriods.forEach((periodKey) => {
      const entry = periodData[periodKey];
      const cumulativeIncome = entry.cumulative;
      const periodIncome = cumulativeIncome - previousCumulative;
      
      data.push({
        period: getPeriodLabel(periodKey),
        income: Math.max(0, periodIncome),
        cumulative: cumulativeIncome,
        target: target
      });
      
      previousCumulative = cumulativeIncome;
    });
    
    return data;
  }, [roiProjection, chartWidth]);

  // Calculate ROI percentage
  const portfolioROI = useMemo(() => {
    if (!roiProjection?.has_data) return 0;
    const { portfolio } = roiProjection;
    return portfolio.total_value > 0 
      ? ((portfolio.total_income - portfolio.total_value) / portfolio.total_value * 100)
      : 0;
  }, [roiProjection]);

  if (roiProjectionLoading) {
    return (
      <Card 
        className={`relative overflow-hidden bg-surface rounded-tile border-0 h-full flex flex-col ${disableLink ? "" : "cursor-pointer"}`}
        onClick={disableLink ? undefined : () => navigate('/equipment')}
      >
        <CardContent className="flex items-center justify-center h-full min-h-[200px]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!roiProjection?.has_data) {
    return (
      <Card 
        className={`relative overflow-hidden bg-surface rounded-tile border-0 h-full flex flex-col ${disableLink ? "" : "cursor-pointer hover:bg-mist transition-colors"}`}
        onClick={disableLink ? undefined : () => navigate('/equipment')}
        data-testid="roi-mini-card"
      >
        <CardHeader className="pb-2 relative px-6 pt-6 flex-shrink-0">
          <div className="flex items-center gap-2">
            <p className="eyebrow leading-tight">income over time</p>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center text-muted-foreground">
          <p className="text-sm">Add equipment to see ROI</p>
        </CardContent>
      </Card>
    );
  }

  const { portfolio } = roiProjection;
  
  // Target line = break-even (total equipment value)
  const targetValue = portfolio.total_value;

  return (
    <Card 
      className={`relative overflow-hidden bg-surface rounded-tile border-0 h-full flex flex-col ${disableLink ? "" : "cursor-pointer hover:bg-mist transition-colors"}`} 
      onClick={disableLink ? undefined : () => navigate('/equipment')}
      data-testid="roi-mini-card"
    >
      <CardHeader className="pb-2 relative px-6 pt-6 flex-shrink-0">
        <div className="flex items-center gap-2">
          <p className="eyebrow leading-tight">income over time</p>
        </div>
      </CardHeader>
      <CardContent className="pt-0 relative px-6 pb-6 flex-1 flex flex-col text-foreground">
        {/* Mini Chart with Period Bars - flex-grow ensures stats align at bottom */}
        {/* Absolutely filled, so the chart never depends on a percentage height
            resolving (Safari leaves one inside a stretched grid cell at zero) */}
        <div ref={chartContainerRef} className="relative min-h-[90px] w-full flex-1">
          <div className="absolute inset-0">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 0, left: 0, bottom: 16 }}>
              <XAxis 
                dataKey="period" 
                tick={{ fontSize: 13, fill: 'hsl(var(--ink-muted))' }}
                axisLine={{ stroke: 'hsl(var(--line))' }}
                tickLine={false}
                interval="preserveStartEnd"
                height={20}
              />
              
              <YAxis hide domain={[0, (dataMax) => {
                // Find max bar height to scale appropriately
                const maxBar = Math.max(...chartData.map(d => d.income || 0));
                // Scale to 120% of max bar height for better visibility
                return maxBar * 1.2;
              }]} />
              
              {/* Tooltip */}
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--ink) / 0.08)' }} />
              
              {/* Target line - break-even, a moss rule */}
              <ReferenceLine 
                y={portfolio.total_value} 
                stroke="hsl(var(--moss))" 
                strokeWidth={1.5}
                label={{ 
                  value: 'Target', 
                  position: 'insideTopLeft',
                  fill: 'hsl(var(--moss))',
                  fontSize: 13,
                  dy: 8,
                  dx: 2
                }}
              />
              
              {/* Period income bars - ink */}
              <Bar
                dataKey="income"
                fill="hsl(var(--ink))"
                fillOpacity={0.85}
                radius={[3, 3, 0, 0]}
                maxBarSize={40}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
          </div>
        </div>

        {/* Stats - Aligned to bottom left */}
        <div className="flex items-end mt-auto">
          <div>
            <p className="text-3xl xl:text-5xl 2xl:text-6xl font-light tracking-tight leading-none tabular-nums">{formatCurrency(portfolio.total_income)}</p>
            <p className="text-sm text-muted-foreground mt-3">Total earned</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
